import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import { Restaurant, CustomerDeliveryAddress } from '@/models';
import { CountryCode } from '@/store/ui';
import { updateBrowserUrl, getTopLevelDomainByCountryCode, getCountryName, isCardPaymentProviderInCountry, getRedirectParams } from '@/helpers';
@Component({
  name: 'CmgCountrySwitchAlertModal',
})
export default class CmgCountrySwitchAlertModal extends Vue {
  isLoading = false;

  get isPickup(): boolean {
    return this.$services.order.isPickingUp();
  }

  get isDelivery(): boolean {
    return !this.isPickup;
  }

  get headerText(): string {
    return this.isPickup ? 'PICKING UP FROM' : 'DELIVERING TO';
  }

  get selectedRestaurant(): Restaurant | null {
    return this.$appStore.state.restaurant.selectedRestaurant;
  }

  get selectedDeliveryAddress(): CustomerDeliveryAddress | null {
    return this.$appStore.state.order.selectedDeliveryAddress;
  }

  get deliveryAddressName(): string | undefined | null {
    return this.selectedDeliveryAddress && this.selectedDeliveryAddress!.name;
  }

  get addressLine1(): string | undefined | null {
    return this.isPickup
      ? this.selectedRestaurant && this.selectedRestaurant!.address.addressLine1
      : this.selectedDeliveryAddress && this.selectedDeliveryAddress!.streetAddress1;
  }

  get city(): string | undefined | null {
    return this.isPickup
     ? this.selectedRestaurant && this.selectedRestaurant!.address.locality 
     : this.selectedDeliveryAddress && this.selectedDeliveryAddress!.city;
  }

  get state(): string | undefined | null {
    return this.isPickup
      ? this.selectedRestaurant && this.selectedRestaurant!.address.administrativeArea
      : this.selectedDeliveryAddress && this.selectedDeliveryAddress!.state;
  }

  get zipCode(): string | undefined | null {
    return this.isPickup
      ? this.selectedRestaurant && this.selectedRestaurant!.address.postalCode
      : this.selectedDeliveryAddress && this.selectedDeliveryAddress!.postalCode;
  }

  get countryCode(): string | undefined | null {
    return this.isPickup
      ? this.selectedRestaurant && this.selectedRestaurant!.address.countryCode
      : this.selectedDeliveryAddress && this.selectedDeliveryAddress!.countryCode;
  }

  get countryName(): string {
    return getCountryName(this.countryCode || 'US').toLocaleUpperCase();
  }

  get countryMessagePath(): string {
    return `modals.countrySwitchAlert.${this.countryCode}.countryMessage`;
  }

  get rewardsPaymentsMessagePath(): string {
    //Checks to give a message about not getting any points when in shopping in different country other than user profile country
    if (this.$services.loyalty.isEnrolledInLoyalty(this.$appStore.state.customer.profile) && 
        this.$appStore.state.customer.profile?.country != this.countryCode) {
      return `modals.countrySwitchAlert.${this.countryCode}.rewardsPaymentsMessageLoyaltyMember`;
    } else {
      return `modals.countrySwitchAlert.${this.countryCode}.rewardsPaymentsMessageNonLoyaltyMember`;
    }
  }

  get paymentMethodButtonTextPath(): string {
    return `modals.countrySwitchAlert.${this.countryCode}.paymentMethodButtonText`;
  }

  get isAboveStorePaymentOnline(): boolean {
    const flags = this.$appStore.state.featureFlags;
    if (flags.orderingStatus) {
      return flags.orderingStatus.aboveStorePaymentOnline 
             && flags.orderingStatus.isPayeezyPaymentOnline 
             && flags.orderingStatus.isChasePaymentOnline 
             && flags.orderingStatus.isUComPaymentOnline
             && flags.orderingStatus.isChaseUKPaymentOnline;
    }

    return true;
  }

  get hasPaymentMethodSaved(): boolean {
    const creditCards = this.$appStore.state.customer.wallet.paymentCards.filter(
      card => card.isGiftCard == false && (isCardPaymentProviderInCountry(this.countryCode!.toString(), card.paymentProviderId!)))
    const giftCards = this.$appStore.state.customer.wallet.paymentCards.filter(card => card.isGiftCard == true);

    // Canada does not currently accept gift cards
    switch (this.countryCode) {
      case CountryCode.CA:
        return !!creditCards.length;
      case CountryCode.US:
      default:
        return !!(creditCards.length + giftCards.length);
    }
  }

  get visible(): boolean {
    return this.$appStore.state.ui.modals.countrySwitchAlertModal.visible;
  }

  onClickOkay(): void {
    this.close();
    const tld = getTopLevelDomainByCountryCode(this.countryCode!);
    const redirectQueryParams = getRedirectParams(this.$appStore.state, this.$services.order);
    updateBrowserUrl(location, tld, redirectQueryParams);
  }

  onClickAddPaymentMethod(): void {
    this.close();
    this.$services.ui.setModalVisibility('addCreditOrDebitCardModal', true, { onClose: () => this.onClickOkay() });
  }

  close() {
    this.isLoading = false;
    this.$services.ui.setModalVisibility('countrySwitchAlertModal', false);
  }
}
